import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  UserPermission
} from '@globaleris/ui.library'
import { Button, CardContent, CardHeader, makeStyles, useTheme } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { getUnusedTranslations } from './translationsCard.helpers'
import {
  translationTableModelMapperToTranslationsModel,
  translationsToTranslationTableModelMapper,
  conceptTranslationTableModelMapperToConceptTranslationsModel
} from './translationsCard.mapper'
import {
  ConceptTranslationModel,
  ConceptTranslationTableModel,
  TranslationModelBase
} from './translationsCard.model'
import { styles } from './translationsCard.styles'
import { LanguageModel } from '../../../api/Argentia/model/contracts'
import { useFormDialog, FormDialogDefinitionProps } from '../../../hooks'
import { generateUniqueKeyWithNoPrefix } from '../../../helpers'
import { getCustomerDomain } from '../../../redux'
import { associateActionIcon } from '../../../helpers/dataTableActionIconHelper'
import { ActionIcons } from '../../../model'
import { LoadingCard } from '../loadingCard/loadingCard'
import { CardWithTransition } from '../cardWithTransition/CardWithTransition'
import { CardEmptyContent } from '../../../pages/products/edit/components/shared/cardEmptyContent/cardEmptyContent'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { Pages } from '../../../model/RBAC/constants'

export interface TranslationsCardProps {
  translations: ConceptTranslationModel[] | undefined | null
  languages: LanguageModel[]
  onAddTranslationsRowValues: (formData: ConceptTranslationModel) => void
  onEditTranslationsRowValues: (rowData: ConceptTranslationModel) => void
  onDeleteTranslationsRowValues: (rowData: ConceptTranslationModel) => void
  isFetching: boolean
}

export interface EditFormDependenciesType {
  formState: any
  translationsOptions: SelectOption[]
}

export const TranslationsCard: React.FC<TranslationsCardProps> = ({
  translations,
  languages,
  onAddTranslationsRowValues,
  onEditTranslationsRowValues,
  onDeleteTranslationsRowValues,
  isFetching
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'conceptsEdit', 'enums', 'dataTable'])
  const history = useHistory()
  const { openForm, closeForm, updateFormDefinition, formProps, CustomFormDialog } = useFormDialog()
  const [editFormDependencies, setEditFormDependencies] = useState<EditFormDependenciesType>({
    formState: {},
    translationsOptions: []
  })

  // Import Styles
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const translationFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_ADD_TRANSLATIONS',
        sections: [
          {
            inputs: [
              {
                name: 'description',
                label: t('conceptsEdit:components.translationsCard.editDialog.description'),
                type: DataType.TEXT
              },
              {
                name: 'language',
                label: t('conceptsEdit:components.translationsCard.editDialog.language'),
                type: DataType.SELECT,
                // defaultValue: editFormDependencies.formState.language,
                selectOptions:
                  editFormDependencies?.translationsOptions?.length > 0
                    ? editFormDependencies?.translationsOptions
                    : [{ value: 0, label: '...' }]
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editFormDependencies])

  //#region Hooks Starts
  useEffect(() => {
    updateFormDefinition(translationFormDefinition, editFormDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationFormDefinition])

  //#endregion Hooks Ends

  if (translations == null || isFetching) {
    return <LoadingCard />
  }

  //#region Handlers Starts
  const onAddTranslationHandler = async (translations: ConceptTranslationModel[]) => {
    const unusedTranslations: SelectOption[] = getUnusedTranslations(translations, languages)

    openForm({
      definition: translationFormDefinition,
      title: t('conceptsEdit:forms.edit.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddTranslationCloseHandler
    })

    setEditFormDependencies({
      formState: {} as ConceptTranslationTableModel,
      translationsOptions: unusedTranslations
    })

    return true
  }

  const onAddTranslationCloseHandler = (formData: ConceptTranslationTableModel) => {
    // Generate tempId to added data to render ActionIcon later
    if (formData) {
      const formDataWithTempId = {
        ...formData,
        tempId: generateUniqueKeyWithNoPrefix()
      }

      // Map back Table to Model and modify state
      onAddTranslationsRowValues(
        conceptTranslationTableModelMapperToConceptTranslationsModel(formDataWithTempId)
      )
    }

    // Clean dependency state
    setEditFormDependencies({} as EditFormDependenciesType)

    closeForm()
  }

  const onEditTranslationHandler = (row, translations: ConceptTranslationModel[]) => {
    const unusedTranslations: SelectOption[] = getUnusedTranslations(translations, languages, row)

    openForm({
      definition: translationFormDefinition,
      title: t('conceptsEdit:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      // inputData: row,
      onClose: onEditTranslationCloseHandler
    })

    setEditFormDependencies({
      ...editFormDependencies,
      formState: row,
      translationsOptions: unusedTranslations
    })
  }

  const onEditTranslationCloseHandler = (row: ConceptTranslationTableModel) => {
    if (row) {
      // Generate tempId to added data to render ActionIcon later
      let rowWithTempId: ConceptTranslationTableModel
      if (row.tempId) {
        rowWithTempId = row
      } else {
        rowWithTempId = {
          ...row,
          tempId: generateUniqueKeyWithNoPrefix()
        }
      }
      // Map back Table to Model and modify state
      onEditTranslationsRowValues(
        conceptTranslationTableModelMapperToConceptTranslationsModel(rowWithTempId)
      )
    }
    // Clean dependency state
    setEditFormDependencies({} as EditFormDependenciesType)

    closeForm()
  }

  const onDeleteTranslationHandler = (row: ConceptTranslationTableModel) => {
    // Map back Table to Model and modify state
    onDeleteTranslationsRowValues(conceptTranslationTableModelMapperToConceptTranslationsModel(row))

    // Clean dependency state
    setEditFormDependencies({} as EditFormDependenciesType)
  }

  //#endregion Handlers Ends

  //#region Form Definition Starts

  //#endregion Form Definition Ends

  //#region DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'description',
      label: t('conceptsEdit:components.translationsCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'language',
      label: t('conceptsEdit:components.translationsCard.dataTable.columns.language'),
      selectOptions: languages.map(l => ({ value: l.id, label: l.description })),
      type: DataType.SELECT
    },
    {
      name: 'default',
      label: t('conceptsEdit:components.translationsCard.dataTable.columns.default'),
      type: DataType.BOOLEAN
    }
  ]

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,

    analytics: {
      customer: getCustomerDomain(),
      view: t('conceptsEdit:components.translationsCard.dataTable.analytics')
    },
    onRowInsert: () => onAddTranslationHandler(translations),
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('conceptsEdit:components.translationsCard.dataTable.actions.edit'),
        onClick: row => onEditTranslationHandler(row, translations),
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('conceptsEdit:components.translationsCard.dataTable.actions.delete'),
        onClick: onDeleteTranslationHandler,
        userPermission: UserPermission.EDIT,
        visibleIf: 'notDefault'
      }
    ]
  }
  //#endregion DataTable Config Ends

  //#region Functions Starts

  const loadNotSaveIconsToTranslationsList = (
    dataTableList: ConceptTranslationModel[]
  ): ConceptTranslationModel[] => {
    if (dataTableList && dataTableList.length > 0) {
      return dataTableList.map(t => {
        if (t.tempId) {
          const actionIcon = associateActionIcon(t, ActionIcons.NEW_ITEM, 'No Guardado')
          return {
            ...t,
            actions: actionIcon
          }
        }
        return t
      })
    }
    return dataTableList
  }

  //#endregion Functions Ends

  if (translations.length === 0) {
    return (
      <>
        <CardWithTransition show={true} raised>
          <CardHeader
            title={t('conceptsEdit:components.translationsCard.title')}
            titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
          />
          <CardEmptyContent title={t('conceptsEdit:components.translationsCard.emptyCard.title')}>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={() => onAddTranslationHandler(translations)}
            >
              {t('conceptsEdit:components.translationsCard.emptyCard.buttonLabel')}
            </Button>
          </CardEmptyContent>
        </CardWithTransition>
        <CustomFormDialog {...formProps} />
      </>
    )
  }

  const translationsList = loadNotSaveIconsToTranslationsList(translations)

  return (
    <>
      <CardWithTransition show={true} raised>
        <CardHeader
          title={t('conceptsEdit:components.translationsCard.title')}
          titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
        />
        <CardContent className={classes.cardContent}>
          <DataTable
            className={classes.dataTableStyle}
            data={translationsToTranslationTableModelMapper(translationsList)}
            columns={columns}
            options={tableOptions}
            userPermissions={getUserPermissions(Pages.Client)}
          />
        </CardContent>
      </CardWithTransition>
      <CustomFormDialog {...formProps} />
    </>
  )
}
